/**
 * Display labels for handoff lifecycle events (SPEC-2253 / SPEC-2300).
 *
 * Kept free of JSX so the labelling — including the one rule that depends on
 * surrounding events — can be unit-tested without rendering the timeline.
 */

export interface TimelineEventLike {
  event?: string;
  meta?: { actionSource?: unknown } | null;
}

/**
 * Where an action was triggered from, sent as `source` on every write and
 * stored on the history event as `meta.actionSource`.
 *
 * The vocabulary is frontend-owned — the backend stores whatever it is handed
 * without validating — so these two constants are the whole of it.
 */
export const HANDOFF_ACTION_SOURCE = {
  smsLandingPage: 'sms_landing_page',
  inApp: 'in_app',
} as const;

// Backend event names → agent-facing labels. Anything unmapped falls back to a
// humanized version of the name, so a new backend event still shows up.
const EVENT_LABEL: Record<string, string> = {
  claim_link_viewed: 'Opened claim link (SMS)',
  claim_link_used: 'Opened claim link (SMS)',
  soft_hold_released: 'SMS link hold expired',
  hard_accepted: 'Hard claim (accepted)',
  hard_claimed: 'Claimed (SMS)',
  call_initiated: 'Call In Progress',
  converted: 'Converted',
  given_back: 'Released (In-App)',
  released: 'Released (SMS)',
  declined: 'Declined (In-App)',
  decline_link_used: 'Opened Decline Link (SMS)',
  hard_expired: 'Expired (72h unclaimed)',
};

export const humanizeEvent = (event: string): string =>
  event.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * The label for one event.
 *
 * `claimed` is the one that needs help: since SPEC-2300 both the SMS landing
 * page and the in-app queue claim through the same endpoint, so the event name
 * alone cannot say where it came from. `meta.actionSource` answers it.
 *
 * When it is absent — a row claimed before the frontend started sending it —
 * the label says plain "Claimed" rather than guessing. Inferring it from the
 * surrounding events gets an agent who opened the link and later claimed from
 * the widget wrong, and a confidently wrong label is worse than no label.
 */
export const resolveEventLabel = (ev: TimelineEventLike): string => {
  const event = ev?.event ?? '';

  if (event === 'claimed') {
    const source = ev?.meta?.actionSource;
    if (source === HANDOFF_ACTION_SOURCE.smsLandingPage) return 'Claimed (SMS)';
    if (source === HANDOFF_ACTION_SOURCE.inApp) return 'Claimed (In-App)';
    return 'Claimed';
  }

  return EVENT_LABEL[event] ?? humanizeEvent(event);
};
